import lodash from 'lodash';

import DateString from '$shared/models/DateString';
import { EventLogTypeLabel } from '$shared/constants/EventLogType';
import RelatedEntityType, { IgnoredRelatedEntityType } from '$shared/constants/RelatedEntityType';
import { AuditLogMessageParser } from './AuditLogMessageParser';
import BaseModel from '$shared/models/BaseModel';
import { UserRoleLabel } from '$shared/constants/Role';
import type EventLogType from '$shared/constants/EventLogType';
import type { IAuditLog } from '$shared/interfaces/api/IAuditLog';
import { EmptyLabelDefault } from '$shared/constants/global';

const DatasetRoutes = {
  Scan: 'datasets/scans',
  Matches: 'datasets/matches'
};

export default class AuditLog extends BaseModel<IAuditLog> {
  declare public id: string;
  declare public timestamp: DateString;
  declare public type: EventLogType;
  declare public context: Record<string, any>;
  declare public messageTemplate: string | undefined;
  declare public messageParameters: Record<string, any>;

  declare private messageParser: AuditLogMessageParser;
  private hasMultipleProvider = false;

  public get date(): string {
    return this.timestamp?.getParsedDateTime() || '';
  }

  public get entityName(): string {
    if (this.provider && !this.hasMultipleProvider)
      return [this.provider?.firstName, this.provider?.lastName].filter(Boolean).join(' ');

    if (this.facility) return this.facility.name;
    return EmptyLabelDefault;
  }

  public get ipAddress(): string {
    return (
      this.context?.user?.ipAddress || this.context?.request?.userIpAddress || EmptyLabelDefault
    );
  }

  public get author(): string {
    return this.context?.user?.email || EventLogTypeLabel[this.type] || EmptyLabelDefault;
  }

  public get requestBody(): Record<string, any> {
    return this.context?.request?.requestBody || null;
  }

  public get authorRole(): string {
    return UserRoleLabel[this.context?.user?.role] || '';
  }

  public get description(): string {
    return (
      this.messageParser?.formatedMessage ||
      this.context?.request?.operation?.capitaliseFirstString().withSpaceAfterCapitalLetters() ||
      this.context?.request?.routeTemplate ||
      EmptyLabelDefault
    );
  }

  public get hyperlinkFilterText(): string {
    return this.messageParser?.hyperlinkText || '';
  }

  public get provider(): Record<string, any> {
    return this.context?.provider;
  }

  public get facility(): Record<string, any> {
    return this.context?.facility;
  }

  public get responseBody(): Record<string, any> {
    return this.context?.request?.responseBody || null;
  }

  public get responseData(): any {
    return this.responseBody?.items || this.responseBody;
  }

  public get relatedEntities(): any[] {
    const tempRes: ObjectType<any> = {};
    const uniqueResult = this.getUniqueEntities();

    // Add Provider if multiple providers in related entities
    if (this.hasMultipleProvider) {
      uniqueResult['provider'] = {
        label: 'Multiple Providers',
        id: this.provider?.id || '',
        type: RelatedEntityType.Provider
      };
    }

    // Add NPI entity only on attaching to provider
    if (this.isNpiRecord) {
      uniqueResult['npi'] = {
        id: this.messageParameters.number,
        type: RelatedEntityType.Npi
      };
    }

    //Group entries based on type
    Object.values(uniqueResult).forEach((item) => {
      if (Object.values(IgnoredRelatedEntityType).includes(item?.type)) {
        return;
      }
      const key = item.type;
      if (tempRes[key]) {
        tempRes[key].entities = [...tempRes[key].entities, item];
        return;
      }
      tempRes[key] = {
        type: item.type,
        entities: [item]
      };
    });

    return Object.values(tempRes) || [];
  }

  public toProviderFloatingList(): CommonList {
    return {
      id: this.provider.id,
      label: this.entityName,
      key: this.provider.id
    };
  }

  public toFloatingList(): CommonList {
    const entityIds: string[] =
      this.context?.relatedEntities
        ?.filter((entity: any) => entity.type !== RelatedEntityType.Provider)
        ?.map((entity: any) => entity.id) || [];
    return {
      id: entityIds.join('_'),
      label: this.hyperlinkFilterText,
      key: entityIds
    };
  }

  private get isNpiRecord(): boolean {
    return ['AttachNPIRecord'].includes(this.context?.request?.operation);
  }

  protected transformResponse(): void {
    this.id = this.response.id;
    this.timestamp = new DateString(this.response.timestamp);
    this.type = this.response.type;
    this.context = this.response.context;
    this.messageTemplate = this.response.messageTemplate;
    this.messageParameters = this.response.messageParameters;
    this.messageParser = new AuditLogMessageParser(
      this.messageTemplate,
      this.messageParameters,
      this.context
    );
    this.hasMultipleProvider = this.checkHasMultipleProvider();
  }

  private getUniqueEntities(): Record<string, any> {
    const uniqueResult = {};

    const setUniqueResult = (key: string, item: any): void => {
      if (uniqueResult[key]) {
        return;
      }
      uniqueResult[key] = item;
    };

    // Removes duplicate entries
    this.context?.relatedEntities?.forEach((item) => {
      if (item.type === RelatedEntityType.Provider && this.hasMultipleProvider) {
        return;
      }
      if (item.type === RelatedEntityType.Report) {
        if (!this.responseData) {
          return;
        }
        const fileName = this.getFilePathFromResponse();
        if (!fileName) {
          return;
        }
        const value = { label: fileName, type: item.type };
        setUniqueResult(item.type, value);
        return;
      }
      if (item.type === RelatedEntityType.ProfileMerge) {
        const value = {
          label: this.messageParameters?.profileType || '',
          id: item.id,
          type: item.type
        };
        setUniqueResult(item.type, value);
        return;
      }
      if (item.type === RelatedEntityType.DatasetScan && Array.isArray(this.responseData)) {
        const datasetScan = this.getDatasetScanFromResponse(item);
        if (!datasetScan) {
          return;
        }
        const value = this.hasMultipleProvider
          ? { label: datasetScan.type, type: item.type }
          : item;
        setUniqueResult(datasetScan.type, value);
        return;
      }
      const key = `${item.type}${item.id}`;
      setUniqueResult(key, item);
    });

    return uniqueResult;
  }

  private checkHasMultipleProvider(): boolean {
    return (
      lodash.uniq(
        this.context?.relatedEntities
          ?.filter((item: any) => item.type === RelatedEntityType.Provider)
          .map((item: any) => item.id)
      ).length > 1
    );
  }

  private getDatasetScanFromResponse(item: any): Record<string, any> {
    switch (this.context?.request?.route) {
      case DatasetRoutes.Matches: {
        return this.responseData.find((match) => match.scan?.id === item.id)?.scan;
      }
      case DatasetRoutes.Scan: {
        return this.responseData.find((scan) => scan.id === item.id);
      }
    }
  }

  private getFilePathFromResponse(): string {
    return this.responseData.path?.split('/').pop() || '';
  }
}
